import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	Alert,
	Button,
	DatePicker,
	Drawer,
	Form,
	Input,
	InputNumber,
	Radio,
	Select,
	Spin,
	message,
} from 'antd';
import moment from 'moment';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
	uuidv4,
} from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	CHARGE_REASON_OPTIONS,
	CHARGE_REASONS,
	CHARGE_UNITS,
	doesChargeReasonCountTowardLeakRate,
	EPA_CERTIFICATION_TYPE_OPTIONS,
	formatContactEpaCertification,
	formatLeakRatePercent,
	formatRefrigerantTypeLabel,
	getRefrigerantEventTypeLabel,
	hasTechnicianCertification,
	isQuantityBearingEventType,
	isWithinTrailingLeakRateWindow,
	isResultBearingEventType,
	projectLeakRatePercent,
	RECOVERY_REASON_OPTIONS,
	RECOVERY_REASONS,
	REFRIGERANT_EVENT_ASSET_PICKER,
	REFRIGERANT_EVENT_RESULTS,
	REFRIGERANT_EVENT_TYPE_ORDER,
	REFRIGERANT_EVENT_TYPES,
	REFRIGERANT_EVENT_WORK_ORDER_PICKER,
	REFRIGERANT_TECHNICIAN_PICKER,
	REFRIGERANT_TYPE_PICKER,
	shouldCaptureChargeReason,
	shouldCaptureLeakSource,
	shouldCaptureRecoveryReason,
	TECHNICIAN_SOURCE_OPTIONS,
	TECHNICIAN_SOURCES,
	toPounds,
	toUtcCalendarDay,
	TRAILING_WINDOW_MONTHS,
	VERIFICATION_STAGE_OPTIONS,
} from '../../utils/RefrigerantUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { refrigerantEventsRestCrudThunks } from '../../thunks/refrigerant_events_thunks';
import { refrigerantTypesRestCrudThunks } from '../../thunks/refrigerant_types_thunks';
import { assetsRestCrudThunks } from '../../thunks/assets_thunks';
import {
	contactsRestCrudThunksForBuyer,
	contactsRestCrudThunksForSupplier,
	getContactsWithInternalTechs,
} from '../../thunks/contacts_thunks';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';
import { withUserType } from '../withUserType';
import OWButton from '../ow_button/OWButton';
import './LogRefrigerantEventDrawer.less';

const FORM_NAME = 'logRefrigerantEventForm';

/**
 * Matches the width the form was designed at as a modal, so converting the chrome did not reflow any
 * field. Wider than the repo's narrow side panels (380) and narrower than its table pickers (50%),
 * which is right for a single-column form.
 */
const DRAWER_WIDTH = 520;

/** Detaches antd's joined segmented control into wrapping pills — see the .less file. */
const PILL_RADIO_GROUP_CLASS = 'refrigerant-pill-radio-group';

interface LogRefrigerantEventDrawerProps {
	visible: boolean;
	onCancel: () => void;
	onSuccess: (event: any) => void;
	/** Prefilled and locked when the drawer is opened from an appliance. */
	asset?: any;
	/**
	 * Prefilled and locked when the drawer is opened from a work order — the Refrigerant Tracking tab
	 * on a WO detail page. The event a tech logs from inside a work order belongs to that work order,
	 * so it is stated rather than re-picked.
	 */
	workOrder?: any;
	/** That appliance's authoritative summary, if the caller has one — powers the live warning. */
	refrigerantSummary?: any;
	/**
	 * Raised above antd's default when this drawer is opened from INSIDE a Modal — the check-out form.
	 * Both chromes default to 1000, and which one paints on top then depends on the order the two
	 * portals happen to land in the body, which is not something to leave to chance when the losing
	 * outcome is a form the tech can see and cannot click.
	 *
	 * Keep any value BELOW 1050. This drawer's fields are mostly Selects and a DatePicker, and antd
	 * renders their popups at `@zindex-dropdown` / `@zindex-picker` = 1050 in a portal of their own —
	 * so a drawer above that line paints over its own dropdowns and the form becomes unfillable. See
	 * `CheckOutRefrigerantSection`'s constant for the whole ladder.
	 */
	zIndex?: number;
	userType?: string;
	assets?: any;
	contacts?: any;
	refrigerantTypes?: any;
	workOrders?: any;
	saving?: boolean;
	createRefrigerantEvent?: (entity) => Promise<any>;
	fetchAssets?: any;
	fetchMultipleAssets?: any;
	fetchContacts?: any;
	fetchMultipleContacts?: any;
	fetchRefrigerantTypes?: any;
	fetchWorkOrders?: any;
	fetchMultipleWorkOrders?: any;
}

/**
 * The Log Refrigerant Event drawer.
 *
 * Three rules from the ledger's design shape this form, and none of them is cosmetic:
 *
 *  1. **`clientEventUuid` is minted once per open, not per submit.** It is the ledger's idempotency
 *     key: a retried POST carrying the same uuid resolves to the row that already exists instead of
 *     inserting a second one. Regenerating it on a retry would double-count pounds into a regulated
 *     number with nothing to detect it.
 *  2. **The required fields depend on the event type**, exactly as the server enforces them —
 *     `amountLb` for a Charge or a Recover, `result` for a leak inspection or a verification test.
 *     Mirroring the rules here turns a 400 into a field error the tech can actually act on.
 *  3. **Nothing derived is sent.** The leak rate in the warning banner is a projection for the human
 *     about to save; the server recomputes the real number from the ledger and ignores anything the
 *     client might have opinions about.
 *  4. **A 608 event must name a CERTIFIED technician, and the certification is SNAPSHOTTED by the
 *     server.** The write is accepted only when either the picked technician's contact already
 *     carries an EPA certification, or this form supplies the technician's name, class and number
 *     itself. When the contact carries one, the client posts NOTHING but the email and the server
 *     copies the certification onto the row — the same treatment `gwpAtEvent` gets, and for the same
 *     reason: a cert renewed next year must not rewrite what last year's record attests to. The
 *     Internal / External switch chooses which of those two shapes the form collects; see
 *     `isCertificationLocked`.
 */
const LogRefrigerantEventDrawer: FC<LogRefrigerantEventDrawerProps> = ({
	visible,
	onCancel,
	onSuccess,
	asset,
	workOrder,
	refrigerantSummary,
	zIndex,
	userType,
	assets,
	contacts,
	refrigerantTypes,
	workOrders,
	saving,
	createRefrigerantEvent,
	fetchAssets,
	fetchMultipleAssets,
	fetchContacts,
	fetchMultipleContacts,
	fetchRefrigerantTypes,
	fetchWorkOrders,
	fetchMultipleWorkOrders,
}) => {
	const [form] = Form.useForm();
	// Minted once per opening of the drawer — see rule 1 above. `useState`'s lazy initializer, not a
	// `useMemo`, because a uuid regenerated by a re-render is exactly the bug this key exists to stop.
	const [clientEventUuid, setClientEventUuid] = useState(() => uuidv4());

	const lockedAssetId = nullSafeGet('id', asset);
	const lockedWorkOrderId = nullSafeGet('id', workOrder);

	/**
	 * The appliance's EFFECTIVE refrigerant, which is what this form is seeded with.
	 *
	 * The asset ROW only carries a code when the unit names one itself: refrigerant is configured in
	 * two places — on the asset type, describing the equipment class, and on the unit — and a unit that
	 * takes its class's answer has `refrigerantTypeCode` null on its own record. The summary does not,
	 * because the server resolves unit-over-class before returning it
	 * (`RefrigerantLeakRateUtil.effectiveRefrigerantFields`), and so does the OpenSearch projection the
	 * index screen's rows come from — which is why the prefill worked from the Assets tab and came up
	 * blank when the drawer was opened from an appliance's own Refrigerant Tracking tab, on a screen
	 * whose summary card was naming the refrigerant at the same time.
	 *
	 * The asset's own code takes precedence over the summary's for the same reason the server resolves
	 * it that way: the unit overrides its class.
	 */
	const effectiveRefrigerantTypeCode =
		nullSafeGet('refrigerantTypeCode', asset) ||
		nullSafeGet('refrigerantTypeCode', refrigerantSummary) ||
		undefined;

	const eventType = Form.useWatch('eventType', form);
	const result = Form.useWatch('result', form);
	const amount = Form.useWatch('amount', form);
	// Watched because the leak-rate projection depends on it: a charge dated outside the trailing
	// 12-month window contributes nothing to the current rate.
	const occurredAt = Form.useWatch('occurredAt', form);
	// Watched for the same reason as the date: only a top-off enters the rate's numerator, so a New
	// Install moves the projection exactly as little as a back-dated charge does.
	const chargeReason = Form.useWatch('chargeReason', form);
	// Watched because which certification fields are REQUIRED flips with the technician — see
	// `isTechnicianCertifiedOnFile`.
	const technicianEmail = Form.useWatch('technicianEmail', form);
	// Internal (pick a colleague) vs External (type an outside contractor's details). Watched rather
	// than held in `useState` so the switch is a normal form field: `resetFields` puts it back to
	// Internal with everything else, and there is no second copy of it to fall out of step.
	const technicianSource = Form.useWatch('technicianSource', form);
	// Watched so the work-order picker can scope itself when the drawer was NOT opened against a
	// locked appliance. Opened from the Assets or Event Log header, `lockedAssetId` is undefined and
	// the appliance is chosen in the form, so this is the only thing that knows which one it is.
	const selectedAssetId = Form.useWatch('assetId', form);

	// The appliance the event is being logged against, wherever it came from. A drawer opened against
	// an asset is locked to it; otherwise it is whatever the picker above currently holds.
	const eventAssetId = lockedAssetId || selectedAssetId;

	const isQuantityBearing = isQuantityBearingEventType(eventType);
	const isResultBearing = isResultBearingEventType(eventType);

	React.useEffect(() => {
		if (!visible) return;
		// no_pagination: the endpoint defaults to 10 rows and there are more refrigerants than that.
		fetchRefrigerantTypes({ no_pagination: true }, REFRIGERANT_TYPE_PICKER);
	}, [visible, fetchRefrigerantTypes]);

	const refrigerantTypeRecords = getRecordsForTargetCollection(
		refrigerantTypes,
		REFRIGERANT_TYPE_PICKER
	);

	/**
	 * The contact behind the technician picker's value.
	 *
	 * The picker's `valueAccessor` is `contact.email`, so the form only ever holds a bare string —
	 * but the certification rule is a fact about the CONTACT, so the record has to be resolved back
	 * out of the same target collection the picker fills. Same shape as `refrigerantTypeRecords`
	 * above.
	 *
	 * `null` for an email with no record in the collection. That cannot normally happen here — the
	 * field has no initial value, so the only way to set it is to pick from the fetched list — and
	 * when it does, it degrades to the manual-entry branch, which is the safe direction: it asks for
	 * a certification rather than assuming one exists.
	 */
	const selectedTechnicianContact = useMemo(
		() =>
			technicianEmail
				? getRecordsForTargetCollection(contacts, REFRIGERANT_TECHNICIAN_PICKER).find(
						(contact) => nullSafeGet('email', contact) === technicianEmail
				  ) || null
				: null,
		[contacts, technicianEmail]
	);

	/**
	 * Rule (a) of the server's acceptance test: this technician's own record already carries both
	 * halves of a 608 certification, so the event needs no manual entry and the server will snapshot
	 * the contact's certification onto the row itself.
	 */
	const isTechnicianCertifiedOnFile = hasTechnicianCertification(selectedTechnicianContact);

	/** Internal is the default; only an explicit switch to External turns the picker into free text. */
	const isInternalTechnician = technicianSource !== TECHNICIAN_SOURCES.EXTERNAL;

	/**
	 * Whether the certification fields are the CONTACT's rather than the user's to fill in.
	 *
	 * The two certification inputs are always on screen now, and this is what decides whether they are
	 * a display of a credential already on file or a pair of empty required fields. Locked means both:
	 * filled from the picked contact and disabled — and, at submit, OMITTED from the payload, because
	 * the server snapshots the contact's own credential and echoing a copy of it from here would write
	 * whatever the picker happened to be holding into a regulated ledger.
	 *
	 * Not locked covers three states that are all the same ask — an external contractor, an internal
	 * tech with nothing on file, and no technician chosen yet — so they get one treatment: type it in.
	 */
	const isCertificationLocked = isInternalTechnician && isTechnicianCertifiedOnFile;

	/**
	 * Whether the certification currently sitting in the form was written there by the effect below
	 * rather than typed by a human.
	 *
	 * Needed because these two fields no longer unmount, so `preserve={false}` cannot clean up after
	 * them. Without it, picking a certified tech, then switching to External, would leave that
	 * colleague's certificate number in an editable field on an event attributed to somebody else — a
	 * real credential against the wrong person, which is the one failure mode this whole section
	 * exists to avoid. A ref rather than state: nothing renders from it, and it must be readable in the
	 * same effect pass that writes it.
	 */
	const isCertificationAutoFilledRef = React.useRef(false);

	/**
	 * Fills the certification from the picked technician's contact and clears it again when that
	 * technician goes away.
	 *
	 * The `errors: []` half matters as much as the value: somebody who submits, gets two red "required"
	 * messages, and then fixes it by picking a certified colleague must not be left staring at
	 * validation state for fields that are now filled in and disabled.
	 *
	 * The clearing branch is guarded on the ref so it only ever discards values this effect wrote. A
	 * blanket clear would wipe an external contractor's number every time an unrelated dependency
	 * moved.
	 */
	React.useEffect(() => {
		if (isCertificationLocked) {
			form.setFields([
				{
					name: 'certificationType',
					value: nullSafeGet('epaCertificationType', selectedTechnicianContact),
					errors: [],
				},
				{
					name: 'certificationNumber',
					value: nullSafeGet('epaCertificationNumber', selectedTechnicianContact),
					errors: [],
				},
			]);
			isCertificationAutoFilledRef.current = true;
		} else if (isCertificationAutoFilledRef.current) {
			form.setFields([
				{ name: 'certificationType', value: undefined, errors: [] },
				{ name: 'certificationNumber', value: undefined, errors: [] },
			]);
			isCertificationAutoFilledRef.current = false;
		}
	}, [form, isCertificationLocked, selectedTechnicianContact]);

	/**
	 * Whether the date on the form puts this charge inside the trailing 12-month window at all.
	 *
	 * A back-dated event is a legitimate and common entry — a tech writing up last year's service
	 * call — and the ledger keeps it. But the server computes `addedLbTrailing12Mo` from
	 * `eventsInWindow` only, so a charge older than 12 months adds nothing to the CURRENT rate.
	 * Without this gate the drawer told the tech the rate was about to jump to 68.6% and then saving
	 * left it unchanged, which on a regulated figure reads as the number being wrong rather than the
	 * warning being wrong.
	 */
	const isInTrailingWindow = useMemo(
		() =>
			isWithinTrailingLeakRateWindow(
				occurredAt,
				// The summary's own instant, so the banner and the figure it is projecting from are
				// measured against the same window. Falls back to now for a caller with no summary.
				nullSafeGet('asOf', refrigerantSummary)
			),
		[occurredAt, refrigerantSummary]
	);

	/**
	 * Whether the reason on the form puts this charge into the leak rate at all.
	 *
	 * Mirrors the server's `RefrigerantChargeReason.countsTowardLeakRate`, including its treatment of
	 * an absent reason as counting — which is what a Charge looks like for the instant between
	 * picking the type and picking the reason, and is also what every pre-existing ledger row looks
	 * like.
	 */
	const doesReasonCountTowardLeakRate = doesChargeReasonCountTowardLeakRate(chargeReason);

	/**
	 * "Logging this takes the 12-month leak rate to X%".
	 *
	 * Only ever shown for a Charge, and only when the appliance's own summary is on hand: pounds added
	 * are the leak rate's numerator, and a Recover does not enter it at all (recovered refrigerant is
	 * not subtracted — a sealed loop that had to be topped up leaked, whatever came back out later).
	 *
	 * Gated on the event's DATE and on its REASON as well as its type. The reason gate is the same
	 * class of bug as the date gate: commissioning a new unit adds pounds without any leak, the server
	 * keeps them out of `addedLbTrailing12Mo`, and a banner promising a jump the save will not produce
	 * reads as the regulated number being wrong rather than the banner being wrong.
	 */
	const projectedLeakRate = useMemo(() => {
		if (eventType !== REFRIGERANT_EVENT_TYPES.CHARGE || !refrigerantSummary) return null;
		if (!doesReasonCountTowardLeakRate) return null;
		if (!isInTrailingWindow) return null;
		const amountLb = toPounds(amount, CHARGE_UNITS.POUND);
		if (amountLb === null) return null;
		return projectLeakRatePercent(
			nullSafeGetWithZero('addedLbTrailing12Mo', refrigerantSummary),
			nullSafeGetWithZero('fullChargeLb', refrigerantSummary),
			amountLb
		);
	}, [eventType, refrigerantSummary, amount, isInTrailingWindow, doesReasonCountTowardLeakRate]);

	/**
	 * Shown in place of the projection when the tech has back-dated a charge out of the window. The
	 * silence would otherwise be ambiguous — it looks identical to "we have no summary for this
	 * appliance" — and the one thing the tech needs to know is that this entry is being recorded but
	 * will not move today's rate.
	 */
	const showOutOfWindowNotice =
		eventType === REFRIGERANT_EVENT_TYPES.CHARGE &&
		!!refrigerantSummary &&
		// Suppressed when the reason already excludes the charge: the two notices say the same thing
		// for two different reasons, and showing both would read as two separate problems.
		doesReasonCountTowardLeakRate &&
		!isInTrailingWindow &&
		!!occurredAt &&
		toPounds(amount, CHARGE_UNITS.POUND) !== null;

	/**
	 * The reason's own version of the out-of-window notice, and it exists for the same reason: the
	 * absence of the projection banner is ambiguous, and the one thing the tech needs to know is that
	 * the pounds they are about to log are recorded but do not move the regulated rate.
	 *
	 * Unlike its out-of-window sibling this does NOT require the appliance's summary. That gate is
	 * there because the sibling replaces a NUMBER the summary supplies; this notice states a rule
	 * about the reason itself, which is equally true on the three mount points that pass no summary.
	 */
	const showNonLeakChargeNotice =
		eventType === REFRIGERANT_EVENT_TYPES.CHARGE &&
		!!chargeReason &&
		!doesReasonCountTowardLeakRate &&
		toPounds(amount, CHARGE_UNITS.POUND) !== null;

	const thresholdPercent = nullSafeGetWithZero('thresholdPercent', refrigerantSummary);
	const willExceedThreshold =
		projectedLeakRate !== null &&
		thresholdPercent !== null &&
		thresholdPercent !== undefined &&
		projectedLeakRate >= Number(thresholdPercent);

	const resetAndClose = useCallback(
		(callback) => {
			form.resetFields();
			// A new drawer is a new event, so it gets a new idempotency key. Keeping the old one would
			// make the second event look like a replay of the first and be silently discarded.
			setClientEventUuid(uuidv4());
			callback();
		},
		[form]
	);

	const handleCancel = useCallback(() => resetAndClose(onCancel), [onCancel, resetAndClose]);

	/**
	 * The work-order picker is scoped to the chosen appliance, so a work order picked BEFORE the
	 * appliance changed is a link to a DIFFERENT appliance's work order — precisely the mislink the
	 * scoping exists to prevent, and one the user cannot see because the field keeps showing the label
	 * it was given. Clearing it is the honest response to the appliance moving.
	 *
	 * Done in the change handler rather than an effect keyed on the asset: both the cause and the
	 * result live inside React, which is the case the repo's guidance says is not an effect's job.
	 */
	const handleValuesChange = useCallback(
		(changedValues) => {
			if ('assetId' in changedValues && form.getFieldValue('workOrderId')) {
				form.setFieldsValue({ workOrderId: undefined });
			}
		},
		[form]
	);

	const handleSubmit = useCallback(
		(values) => {
			const assetId = lockedAssetId || values.assetId;
			const workOrderId = lockedWorkOrderId || values.workOrderId;
			const amountLb = isQuantityBearingEventType(values.eventType)
				? toPounds(values.amount, CHARGE_UNITS.POUND)
				: undefined;

			// Every optional field is OMITTED rather than sent as null: the server reads them with
			// optValueString/optValueBigDecimal, which turn a JSON null into a corrupt Some(None) rather
			// than an absent value. Same hazard the asset form's payload builder documents. That applies
			// to the certification triple and the charge reason exactly as it does to the rest — this
			// builder previously sent no certification fields at all, which is the gap that made an
			// uncertified technician's event indistinguishable from a certified one's.
			const entity = {
				assetId,
				eventType: values.eventType,
				occurredAt: toUtcCalendarDay(values.occurredAt),
				clientEventUuid,
				...(amountLb !== undefined && amountLb !== null && { amountLb }),
				...(values.refrigerantTypeCode && { refrigerantTypeCode: values.refrigerantTypeCode }),
				...(values.technicianEmail && { technicianEmail: values.technicianEmail }),
				...(workOrderId && { workOrderId }),
				...(values.result && { result: values.result }),
				...(values.verificationStage && { verificationStage: values.verificationStage }),
				// Gated on the type as well as on the value. `preserve={false}` already drops the field
				// when the Charge branch unmounts, but a reason on a Recover would be a meaningless
				// column on a regulated row, and the type is what actually decides whether it means
				// anything.
				...(shouldCaptureChargeReason(values.eventType) &&
					values.chargeReason && { chargeReason: values.chargeReason }),
				// The recovery half of the same rule, gated on the type for the same reason. This is
				// what carries what a `Disposal` event type used to say by itself, and the server
				// rejects a Recover without it.
				...(shouldCaptureRecoveryReason(values.eventType) &&
					values.recoveryReason && { recoveryReason: values.recoveryReason }),
				// `technicianName` is only ever in `values` on the External branch — the field unmounts
				// on Internal and `preserve={false}` drops it — which is correct: on the Internal branch
				// the SERVER stamps the contact's display name onto the row.
				...(values.technicianName && { technicianName: values.technicianName }),
				// Sent only when the user actually supplied them. When the picked technician's contact
				// carries a certification the two inputs are filled and disabled for display, and their
				// values are deliberately DROPPED here: the server snapshots that contact's own class,
				// number and name onto the row, the way it snapshots `gwpAtEvent`, so echoing a copy
				// from the picker would write whatever this client happened to be holding into a
				// regulated ledger. `isCertificationLocked`, not a value test — the values are present
				// in exactly that case.
				...(!isCertificationLocked &&
					values.certificationType && { certificationType: values.certificationType }),
				...(!isCertificationLocked &&
					values.certificationNumber && { certificationNumber: values.certificationNumber }),
				...(values.leakSource && { leakSource: values.leakSource }),
				...(values.notes && { notes: values.notes }),
			};

			return createRefrigerantEvent(entity)
				.then((created) => {
					message.success('Refrigerant event logged.');
					resetAndClose(() => onSuccess(created));
				})
				.catch((error) =>
					message.error(error || 'Could not log the refrigerant event. Please try again.')
				);
		},
		[
			clientEventUuid,
			createRefrigerantEvent,
			isCertificationLocked,
			lockedAssetId,
			lockedWorkOrderId,
			onSuccess,
			resetAndClose,
		]
	);

	return (
		<Drawer
			visible={visible}
			title="Log Refrigerant Event"
			onClose={handleCancel}
			placement="right"
			width={DRAWER_WIDTH}
			zIndex={zIndex}
			destroyOnClose={true}
			footer={
				<div className="flex flex-row justify-end gap-2">
					<Button onClick={handleCancel}>Cancel</Button>
					{/* OWButton, not Button: it swallows a second click while the first is in flight. That
					    matters more here than on an ordinary form — a double submit would post the same
					    event twice, and although `clientEventUuid` makes the server discard the replay,
					    the guard is what stops it ever depending on that.

					    The submit stays bound through the HTML `form=` attribute rather than an onClick,
					    so antd's own validation runs before `handleSubmit` sees anything. A Drawer footer
					    portals into the same document as its body, so the association works exactly as it
					    did in the modal. */}
					<OWButton type="primary" form={FORM_NAME} htmlType="submit" loading={saving}>
						Continue
					</OWButton>
				</div>
			}
		>
			{asset ? (
				<div className="mb-4 flex flex-col">
					<span className="h5medium text-character-primary">{nullSafeGet('name', asset)}</span>
					<span className="footnoteregular text-character-secondary">
						{[
							nullSafeGet('assetNumber', asset) && `Asset #${nullSafeGet('assetNumber', asset)}`,
							nullSafeGet('serialNumber', asset) && `Serial #${nullSafeGet('serialNumber', asset)}`,
						]
							.filter(Boolean)
							.join(' · ')}
					</span>
				</div>
			) : null}

			<Form
				form={form}
				id={FORM_NAME}
				layout="vertical"
				preserve={false}
				onFinish={handleSubmit}
				onValuesChange={handleValuesChange}
				/*
				  `chargeReason` and `recoveryReason` are DEFAULTED here rather than left blank, and both
				  defaults are the overwhelmingly common case: nearly every charge is a top-off and nearly
				  every recovery is a servicing pump-down. Both fields are still required, so the default
				  is a pre-selection the tech can change, not a value the form decides on their behalf —
				  and the two notices under the amount say plainly when a non-default reason takes the
				  pounds out of the leak rate.

				  They work across event-type switches because `preserve={false}` drops an unmounted
				  field's value, so remounting the Charge branch re-applies `initialValues` rather than
				  restoring whatever was picked last time.

				  `technicianSource` defaults to Internal: the picker produces the better record, since the
				  server snapshots the contact's own credential instead of trusting typed text.
				*/
				initialValues={{
					eventType: REFRIGERANT_EVENT_TYPES.CHARGE,
					occurredAt: moment(),
					refrigerantTypeCode: effectiveRefrigerantTypeCode,
					workOrderId: lockedWorkOrderId || undefined,
					technicianSource: TECHNICIAN_SOURCES.INTERNAL,
					chargeReason: CHARGE_REASONS.TOP_OFF,
					recoveryReason: RECOVERY_REASONS.RECOVER,
				}}
			>
				{!asset ? (
					<Form.Item
						name="assetId"
						label="Asset"
						rules={[{ required: true, message: 'Select the appliance this event happened on.' }]}
					>
						<OWAsyncSelect
							stateSlice={assets}
							targetCollectionName={REFRIGERANT_EVENT_ASSET_PICKER}
							placeholder="Type to search for an appliance"
							allowClear
							// Only refrigerant-tracked appliances can carry a 608 event, so the picker must not
							// offer anything else — a event on an untracked asset is an unbandable orphan.
							additionalFilters={{ isRefrigerantTracked: true }}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
							fetchMultiple={(ids, targetCollectionName) =>
								fetchMultipleAssets(ids, targetCollectionName)
							}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) =>
								fetchAssets(
									{ search: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								)
							}
							renderRecord={(record) => (
								<Select.Option key={record.id} value={record.id}>
									<div className="flex flex-col">
										<span>{record.name}</span>
										<span className="footnoteregular text-character-secondary">
											{nullSafeGet('location.name', record) || nullSafeGet('locationName', record)}
										</span>
									</div>
								</Select.Option>
							)}
						/>
					</Form.Item>
				) : null}

				<Form.Item
					name="occurredAt"
					label="Date"
					rules={[{ required: true, message: 'When did this happen?' }]}
				>
					<DatePicker
						className="w-full"
						format="MMM DD, YYYY"
						// The server refuses a future occurredAt outright, and for a reason worth honouring
						// here: the projection is only recomputed by a write, so an event dated forward would
						// stay missing from the index screen even after its date passed.
						// Bounded on TODAY IN UTC, not local. `occurredAt` is serialized as UTC midnight of the
						// picked day (see toUtcCalendarDay) and the server refuses a future instant, so a
						// picker bounded on the LOCAL day let a tech east of UTC choose a date whose UTC
						// midnight had not arrived yet and get a 400. The two bounds have to agree.
						disabledDate={(current) =>
							current && current.format('YYYY-MM-DD') > moment.utc().format('YYYY-MM-DD')
						}
					/>
				</Form.Item>

				<Form.Item
					name="refrigerantTypeCode"
					label="Refrigerant"
					// Not required: refusing the write over it would lose the 608 record entirely. The
					// server falls back to the appliance's own refrigerant type when it is absent, and
					// snapshots that type's GWP onto the row so a later retrofit cannot rewrite history.
					//
					// Shown for EVERY event type, not just the quantity-bearing ones. The server stamps
					// gwpAtEvent from this code on any event, and the design puts the field above Event
					// Type — a visibility condition in that position would make it appear and vanish from
					// the middle of the form as the type is picked.
					extra="Defaults to the appliance's recorded refrigerant when left blank."
				>
					<Select
						placeholder="Select refrigerant"
						allowClear
						notFoundContent={
							refrigerantTypes.fetching ? <Spin size="small" /> : 'No refrigerant types'
						}
					>
						{refrigerantTypeRecords.map((refrigerantType) => (
							<Select.Option value={refrigerantType.code} key={refrigerantType.code}>
								{formatRefrigerantTypeLabel(refrigerantType)}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{/* ── Technician ───────────────────────────────────────────────────────
				    Internal picks a colleague out of the contacts list; External is free text for an
				    outside contractor who has no record here. The switch decides only which fields are on
				    screen — the server's rule is unchanged and unaware of it: an event is accepted iff
				    EITHER the named contact carries both certification halves, OR the event itself carries
				    technician name, class and number. Internal is the left-hand path, External the right,
				    and an internal tech with nothing on file falls through to typing the certification —
				    which is how "record it by hand" stays one uniform rule rather than a persona-specific
				    second one. */}
				<Form.Item name="technicianSource" label="Technician">
					<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
						{TECHNICIAN_SOURCE_OPTIONS.map((option) => (
							<Radio.Button key={option.value} value={option.value}>
								{option.label}
							</Radio.Button>
						))}
					</Radio.Group>
				</Form.Item>

				{isInternalTechnician ? (
					<Form.Item
						name="technicianEmail"
						// REQUIRED on this branch, where it was optional when the manual triple was the
						// alternative on the same screen. Now that External is a deliberate choice, an
						// Internal event with nobody picked satisfies neither of the server's two shapes —
						// it has no email and no typed name — so it can only ever come back as a 400.
						rules={[{ required: true, message: 'Who performed the work?' }]}
					>
						<OWAsyncSelect
							stateSlice={contacts}
							targetCollectionName={REFRIGERANT_TECHNICIAN_PICKER}
							placeholder="Who performed the work?"
							allowClear
							// The ledger stores the technician's EMAIL, not a contact id — contacts in this
							// codebase are keyed by email and have no numeric id to join to.
							valueAccessor={(contact) => contact.email}
							fetchMultiple={(ids, targetCollectionName) =>
								fetchMultipleContacts(ids, targetCollectionName)
							}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) =>
								fetchContacts(
									{ search: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								)
							}
							renderRecord={(contact) => {
								const certification = formatContactEpaCertification(contact);
								return (
									<Select.Option key={contact.email} value={contact.email}>
										{/* The certification under the name, because it is the fact that decides
										    whether this pick is enough on its own — the option that reads "no EPA
										    certification on file" is the one that is about to ask for two more
										    fields, and knowing that before choosing beats discovering it after.
										    Both states are stated: silence on an uncertified tech would read as a
										    list that only sometimes shows credentials. */}
										<div className="flex flex-col">
											<span>
												{`${nullSafeGetOrElse('nameGiven', contact, '')} ${nullSafeGetOrElse(
													'nameFamily',
													contact,
													''
												)}`.trim() || contact.email}
											</span>
											<span className="footnoteregular text-character-secondary">
												{certification ? `EPA ${certification}` : 'No EPA certification on file'}
											</span>
										</div>
									</Select.Option>
								);
							}}
						/>
					</Form.Item>
				) : (
					<Form.Item
						name="technicianName"
						label="Technician Name"
						extra="The contractor who performed the work, as it should read on the 608 record."
						rules={[{ required: true, message: 'Name the technician who performed the work.' }]}
					>
						<Input placeholder="e.g. Dana Reyes" maxLength={255} />
					</Form.Item>
				)}

				{/* ── Technician EPA 608 certification ──────────────────────────────────
				    Always on screen, in both branches, and either a filled-and-disabled display of what is
				    already on file or two empty required fields. Showing them in one place rather than
				    swapping a read-only summary for a form is what makes "this event attests to THIS
				    certificate" a single stable pair of fields wherever the technician came from.

				    Disabled is not the same as absent: `isCertificationLocked` also drops these values
				    from the payload, so a locked pair is a display of the credential the SERVER will
				    snapshot, not a client-supplied copy of it. See the payload builder. */}
				{isInternalTechnician && technicianEmail && !isTechnicianCertifiedOnFile ? (
					<Alert
						// `!mb-4`, not `mb-4`: antd's own `.ant-alert { margin: 0 }` is injected by
						// babel-plugin-import AFTER tailwind.out.css, so at equal specificity the later rule
						// wins and the plain utility was a no-op — the banner sat flush against the "EPA
						// Certification Type" label below it. The important modifier outranks the reset
						// whatever order the two stylesheets land in.
						className="!mb-4"
						showIcon
						type="warning"
						message="The selected technician has no EPA certification on file. Record the certification this event attests to, or add it to their contact record so it is snapshotted automatically next time."
					/>
				) : null}

				<Form.Item
					name="certificationType"
					label="EPA Certification Type"
					// `dependencies` on both inputs to the lock, so switching between two UNCERTIFIED techs
					// — or flipping Internal/External — re-runs these rules instead of leaving whatever the
					// previous validation decided standing.
					dependencies={['technicianEmail', 'technicianSource']}
					rules={[
						{
							required: true,
							message: "Record the technician's EPA 608 certification class.",
						},
					]}
					extra={
						isCertificationLocked
							? 'On file for this technician. The server records it against this event.'
							: undefined
					}
				>
					<Select placeholder="Select certification type" disabled={isCertificationLocked}>
						{EPA_CERTIFICATION_TYPE_OPTIONS.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="certificationNumber"
					label="EPA Certification Number"
					dependencies={['technicianEmail', 'technicianSource']}
					rules={[
						{
							required: true,
							message: 'Record the certification number this event attests to.',
						},
					]}
				>
					{/* 64 is `certification_number`'s column width. */}
					<Input placeholder="e.g. 5510-114" maxLength={64} disabled={isCertificationLocked} />
				</Form.Item>

				{/* Locked, and STATED rather than left as a picker holding a value. The asset block above
				    does the same thing for the same reason: OWAsyncSelect hydrates a preset value with a
				    follow-up fetch, so a live picker would sit blank for a beat and then fill itself in,
				    which reads as the field being editable and empty. */}
				{lockedWorkOrderId ? (
					<Form.Item label="Work Order">
						<span className="bodyregular text-character-primary">
							{`#WO ${lockedWorkOrderId} ${nullSafeGetOrElse('title', workOrder, '')}`.trim()}
						</span>
					</Form.Item>
				) : (
					<Form.Item
						name="workOrderId"
						label="Work Order"
						extra="Optional — an unplanned top-off is still a valid 608 event."
					>
						{/* `additionalFilters` is scoped to `eventAssetId`, not `lockedAssetId`. Keyed off the
						    locked asset alone, the filter simply vanished whenever the drawer was opened from
						    a list header — the picker then searched the whole tenant and happily let an event
						    for appliance A be attached to appliance B's work order, which the server either
						    rejects or stores as a mislinked 608 row. */}
						<OWAsyncSelect
							stateSlice={workOrders}
							targetCollectionName={REFRIGERANT_EVENT_WORK_ORDER_PICKER}
							placeholder="Link to a work order"
							allowClear
							{...(eventAssetId && { additionalFilters: { assetId: eventAssetId } })}
							fetchMultiple={(ids, targetCollectionName) =>
								fetchMultipleWorkOrders(ids, targetCollectionName)
							}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) =>
								fetchWorkOrders(
									{ search: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								)
							}
							renderRecord={(record) => (
								<Select.Option key={record.id} value={record.id}>
									{`#${record.id} ${nullSafeGetOrElse('title', record, '')}`}
								</Select.Option>
							)}
						/>
					</Form.Item>
				)}

				<Form.Item name="eventType" label="Event Type" rules={[{ required: true }]}>
					<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
						{REFRIGERANT_EVENT_TYPE_ORDER.map((type) => (
							<Radio.Button key={type} value={type}>
								{getRefrigerantEventTypeLabel(type)}
							</Radio.Button>
						))}
					</Radio.Group>
				</Form.Item>

				{shouldCaptureChargeReason(eventType) ? (
					<Form.Item
						name="chargeReason"
						label="Reason for Charge"
						rules={[
							{
								required: true,
								// REQUIRED, unlike the leak source, and the asymmetry is deliberate: the
								// reason is what decides whether these pounds enter the trailing-12-month
								// leak rate at all. Only a top-off is evidence of a leak; commissioning a
								// new unit or retrofitting it to another refrigerant adds refrigerant with
								// nothing having escaped. Leaving it blank would put a new install into a
								// regulated figure and can band a healthy appliance as Exceeded. The server
								// rejects a Charge without it for the same reason, so asking here turns a
								// 400 into a field.
								message: 'Why was refrigerant added? Only a top-off counts toward the leak rate.',
							},
						]}
					>
						<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
							{CHARGE_REASON_OPTIONS.map((option) => (
								<Radio.Button key={option.value} value={option.value}>
									{option.label}
								</Radio.Button>
							))}
						</Radio.Group>
					</Form.Item>
				) : null}

				{shouldCaptureRecoveryReason(eventType) ? (
					<Form.Item
						name="recoveryReason"
						label="Reason for Recovery"
						extra="Disposal means the refrigerant went for reclaim or destruction rather than back into the appliance."
						rules={[
							{
								required: true,
								// REQUIRED, like the charge reason, and it carries what a separate Disposal
								// event type used to say by itself. Both procedures pull the same measured
								// quantity out of the machine and report it into the same recovered total;
								// this field is the only thing on the row that says whether the appliance
								// was serviced or emptied for good, and it is what the EPA disposal record
								// points at. The server rejects a Recover without it.
								message: 'Why was refrigerant recovered?',
							},
						]}
					>
						<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
							{RECOVERY_REASON_OPTIONS.map((option) => (
								<Radio.Button key={option.value} value={option.value}>
									{option.label}
								</Radio.Button>
							))}
						</Radio.Group>
					</Form.Item>
				) : null}

				{isQuantityBearing ? (
					<Form.Item
						name="amount"
						/* A Recover takes refrigerant OUT, so labelling its quantity "Amount added" would
						   misname a regulated figure in the exact opposite direction from what it records.
						   One label covers both recovery reasons: "quantity recovered" is what the EPA
						   disposal record calls the field too, so the wording does not have to move when
						   the reason does. */
						label={
							eventType === REFRIGERANT_EVENT_TYPES.RECOVER ? 'Amount recovered' : 'Amount added'
						}
						rules={[
							{
								required: true,
								message: 'Enter the quantity — it is the regulated number for this event.',
							},
							{
								// `required` alone lets 0 through: antd treats 0 as a present value, not an
								// empty one. A zero-quantity Charge, Recover or Disposal is an append-only
								// ledger row attesting to a measured operation that moved no refrigerant —
								// it cannot be edited away afterwards, and it feeds the leak-rate numerator
								// as a real data point. Refuse it here rather than write it.
								validator: (_, value) =>
									value === null || value === undefined || Number(value) > 0
										? Promise.resolve()
										: Promise.reject(new Error('The quantity has to be greater than zero.')),
							},
						]}
					>
						{/* 3 dp is `amount_lb`'s precision. A 0.01 lb error on a small appliance moves the
						    computed leak rate enough to flip a band, so nothing is truncated here.

						    Pounds is the only unit offered and the only one the ledger stores, so the unit is
						    a fixed addon rather than a picker. It stays VISIBLE: this number is the leak
						    rate's numerator, and a bare box invites a kg entry that would be read as lb. */}
						<InputNumber
							style={{ width: '100%' }}
							// The smallest value `amount_lb`'s 3 dp can express, so the stepper cannot walk the
							// field down to a zero the validator above would then reject.
							min={0.001}
							precision={3}
							addonAfter="lb"
							placeholder="0.000"
						/>
					</Form.Item>
				) : null}

				{isResultBearing ? (
					<Form.Item
						name="result"
						label="Result"
						rules={[
							{
								required: true,
								// An inspection with no result advances the next-inspection date, so the
								// appliance reads as inspected and on schedule while recording nothing about
								// whether a leak was found — a record that looks clean because the observation
								// is missing. The server rejects it for the same reason.
								message: 'Record the result — an inspection with no outcome is not a record.',
							},
						]}
					>
						<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
							<Radio.Button value={REFRIGERANT_EVENT_RESULTS.PASS}>Pass</Radio.Button>
							<Radio.Button value={REFRIGERANT_EVENT_RESULTS.FAIL}>Fail — leak found</Radio.Button>
						</Radio.Group>
					</Form.Item>
				) : null}

				{eventType === REFRIGERANT_EVENT_TYPES.VERIFICATION_TEST ? (
					<Form.Item
						name="verificationStage"
						label="Verification stage"
						extra="A repair is verified twice — an initial test and a follow-up."
						rules={[
							{
								required: true,
								// The server REJECTS a VerificationTest with no stage
								// (`requireStageForVerificationTests`), so without this the write returns a 400
								// the tech cannot act on. The gate above is `eventType === VERIFICATION_TEST`,
								// exactly the server's own condition, so this can never refuse a request the
								// server would have accepted.
								//
								// It is the stage that keeps the two tests apart: a repair carries an initial
								// test and a follow-up within 30 days, and without it the ledger holds two
								// indistinguishable passes and an auditor cannot tell a discharged obligation
								// from a repeated initial test.
								message: 'Which test is this — the initial one or the 30-day follow-up?',
							},
						]}
					>
						<Radio.Group className={PILL_RADIO_GROUP_CLASS} buttonStyle="solid">
							{VERIFICATION_STAGE_OPTIONS.map((option) => (
								<Radio.Button key={option.value} value={option.value}>
									{option.label}
								</Radio.Button>
							))}
						</Radio.Group>
					</Form.Item>
				) : null}

				{shouldCaptureLeakSource(eventType, result) ? (
					<Form.Item name="leakSource" label="Leak Source">
						{/* Free text and optional by design: it is typed on a ladder in a mechanical room, and
						    refusing the write over it would lose the whole event rather than one field. */}
						<Input placeholder="e.g. Compressor rack" maxLength={255} />
					</Form.Item>
				) : null}

				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={3} placeholder="Anything the next tech should know" />
				</Form.Item>

				{/* The three closing notices, stacked. Wrapped rather than each carrying its own margin
				    because two of them can be on screen at once — a back-dated top-off raises the
				    projection AND falls outside the trailing window — and antd's `.ant-alert { margin: 0 }`
				    lands after tailwind.out.css, so a `mb-*` on the alerts themselves is a no-op. `gap` on
				    the wrapper is not subject to that: nothing in antd's stylesheet competes for it.

				    Rendered only when at least one notice is: an empty flex wrapper is inert inside this
				    block-level Form, but it is the shape that eats a row gap the moment a subtree like this
				    is moved under a flex or grid parent. */}
				{projectedLeakRate !== null || showOutOfWindowNotice || showNonLeakChargeNotice ? (
					<div className="flex flex-col gap-4">
						{projectedLeakRate !== null ? (
							<Alert
								showIcon
								type={willExceedThreshold ? 'error' : 'info'}
								message={
									willExceedThreshold
										? `Logging this takes the 12-month leak rate to ${formatLeakRatePercent(
												projectedLeakRate
										  )}, over the ${thresholdPercent}% threshold for this appliance.`
										: `Logging this takes the 12-month leak rate to ${formatLeakRatePercent(
												projectedLeakRate
										  )}${
												thresholdPercent === null || thresholdPercent === undefined
													? '.'
													: ` (threshold ${thresholdPercent}%).`
										  }`
								}
							/>
						) : null}

						{showOutOfWindowNotice ? (
							<Alert
								showIcon
								type="info"
								message={`This charge is dated outside the last ${TRAILING_WINDOW_MONTHS} months, so it is recorded in the ledger but does not change the current 12-month leak rate.`}
							/>
						) : null}

						{showNonLeakChargeNotice ? (
							<Alert
								showIcon
								type="info"
								message="Only a top-off counts toward the leak rate, so this charge is recorded in the ledger but does not change the 12-month leak rate or the CO2e figure derived from it."
							/>
						) : null}
					</div>
				) : null}
			</Form>
		</Drawer>
	);
};

const mapStateToProps = (state) => ({
	assets: state.assets,
	// `contacts_reducer` is mounted as `all_contacts`, not `contacts` — there is no `state.contacts`
	// slice, and reading one leaves OWAsyncSelect with an undefined stateSlice and crashes its render.
	contacts: state.all_contacts,
	refrigerantTypes: state.refrigerant_types,
	workOrders: state.work_orders,
	saving: state.refrigerant_events.creating,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
	const contactThunks = isSupplier
		? contactsRestCrudThunksForSupplier
		: contactsRestCrudThunksForBuyer;
	const workOrderThunks = isSupplier
		? workOrdersRestCrudThunksForSupplier
		: workOrdersRestCrudThunksForBuyer;

	return {
		createRefrigerantEvent: (entity) =>
			dispatch(refrigerantEventsRestCrudThunks(ownProps.userType).create(entity)),
		fetchRefrigerantTypes: (params, targetCollectionName) =>
			dispatch(
				refrigerantTypesRestCrudThunks(ownProps.userType).read(params, targetCollectionName)
			),
		fetchAssets: (params, targetCollectionName, pagination, sorting, filters, add) =>
			dispatch(
				assetsRestCrudThunks(ownProps.userType).read(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add
				)
			),
		fetchMultipleAssets: (ids, targetCollectionName) =>
			dispatch(assetsRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
		// `getContactsWithInternalTechs`, NOT the bare `contactThunks.read`, and this is a fix rather
		// than a preference: `contactsRestCrudThunksForBuyer/Supplier` carry the route prefix
		// `common_contacts` with no sub-path, so `.read` resolves to `GET /api/v1/<role>/common_contacts`
		// — a route that DOES NOT EXIST on either persona. `conf/buyer.routes` and `conf/supplier.routes`
		// expose only `/common_contacts/multiple/:ids`, `/buyer_internal_team_users_list` and
		// `/buyer_internal_techs_active_contacts_list`. So this picker had been fetching a 404 and
		// showing an empty list from the day it was written; every other consumer in the app happens to
		// use `readMultiple`, which maps to the route that does exist, which is why nobody noticed.
		//
		// That was cosmetic while the technician was optional. It stopped being cosmetic when the
		// certification rule landed: picking a certified contact is now one of only two ways to satisfy
		// the server, and an empty picker makes it unreachable — leaving the manual triple as the only
		// path even for a technician whose credential is already on file.
		//
		// The internal-techs variant additionally routes through `contactsEmailAdaptor`, which lifts
		// `epaCertificationType` / `epaCertificationNumber` onto the top level. Without that the fields
		// sit at `record.contact.*`, `hasTechnicianCertification` reads `undefined`, and every certified
		// technician reads as uncertified.
		fetchContacts: (params, targetCollectionName, pagination, sorting, filters, add) =>
			dispatch(
				getContactsWithInternalTechs(ownProps.userType).read(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add
				)
			),
		// `readMultiple` stays on the plain thunk: it maps to `/common_contacts/multiple/:ids`, which is
		// a real route, and it is what hydrates a preset technician value.
		fetchMultipleContacts: (ids, targetCollectionName) =>
			dispatch(contactThunks.readMultiple(ids, targetCollectionName)),
		// `search`-family, not `read`: read hits the SQL-backed list endpoint, whose `search` is a LIKE
		// over a couple of columns and cannot match on the things a tech types to find a work order
		// (location, asset, problem type, note text).
		//
		// `searchAndHydrate`, NOT plain `search`, and the reason is where the results LAND rather than
		// what they contain. `search` dispatches `fetchSearchSuccess`, which stores entities under
		// `state.work_orders.searchRecords`; `OWAsyncSelect` reads its options through
		// `getRecordsForTargetCollection`, which resolves `recordsIds` against `state.work_orders
		// .records`. So plain `search` left this dropdown EMPTY on a successful request. (The tables
		// that do use plain `search` pass `preferSearchRecords`, which is the other half of the same
		// split.) Hydrate's follow-up `ids=` round trip is what puts the hits where the dropdown looks.
		fetchWorkOrders: (params, targetCollectionName, pagination, sorting, filters, add) =>
			dispatch(
				workOrderThunks.searchAndHydrate(
					params,
					targetCollectionName,
					pagination,
					sorting,
					filters,
					add,
					workOrderSearchAndAPIAdaptor
				)
			),
		fetchMultipleWorkOrders: (ids, targetCollectionName) =>
			dispatch(workOrderThunks.readMultiple(ids, targetCollectionName)),
	};
};

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(LogRefrigerantEventDrawer as any) as any
);
